import React, { useCallback, useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import CommaConvert from "../../../../utils/CommaConvert";
import {
  formatAmountComma,
  parseAmountNumber,
} from "../../../../utils/decimalAmountUtils";
import {
  applyLiveTrainingTotalToOtherExpenseRows,
  consolidateTrainingExpenseRowsForDisplay,
  filterInstallationOngoingOtherExpenseDisplayRows,
  getLiveTrainingRollupForOpsAssigneeProject,
  isTrainingExpenseDisplayRow,
} from "../../../../utils/trainingOtherExpenseSync";

const OmShowAppprovedTasks = ({
  employees,
  row,
  projects,
  descriptionProjectTracking,
  hardwareProjectTracking,
  entries,
  tasks,
  items,
  mytrainings,
  showListState,
  isViewOnly = false,
}) => {
  const [matchedProjects, setMatchedProjects] = useState([]);
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();

  useEffect(() => {
    if (projects?.length && row) {
      const matchedProjects = [];
      // IMPORTANT: "Old" project should be matched by stable identifiers (lead / quotation / projectid),
      // not only by name/service — otherwise we can accidentally pick a different lead from earlier buckets.
      const resolveRefId = (ref) => {
        if (ref == null || ref === "") return "";
        if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
        return String(ref).trim();
      };
      const pick = (obj, keys) => {
        for (const k of keys) {
          const val = obj?.[k];
          if (val != null && String(val).trim() !== "") return val;
        }
        return "";
      };
      const normKey = (v) => String(v || "").trim().toLowerCase();

      const rowLeadLike = row?.lead || row?.Lead || null;
      const rowLeadId = String(pick(row, ["leadid", "leadId", "LeadId", "LeadID"]) || pick(rowLeadLike, ["leadid", "leadId", "LeadId", "LeadID"]) || "").trim();
      const rowQuotationId = String(
        pick(row, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]) ||
          pick(rowLeadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]) ||
          ""
      ).trim();
      const rowProjectId = normKey(
        resolveRefId(pick(row, ["_id", "id"])) ||
          resolveRefId(pick(row, ["projectid", "projectId", "ProjectId"])) ||
          resolveRefId(pick(rowLeadLike, ["_id", "id"]))
      );

      const old = projects.find((pro) => {
        if (!pro || pro.isProjecttracking !== false) return false;
        const proLeadLike = pro?.lead || pro?.Lead || null;
        const proLeadId = String(pick(pro, ["leadid", "leadId", "LeadId", "LeadID"]) || pick(proLeadLike, ["leadid", "leadId", "LeadId", "LeadID"]) || "").trim();
        const proQuotationId = String(
          pick(pro, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]) ||
            pick(proLeadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]) ||
            ""
        ).trim();
        const proProjectId = normKey(
          resolveRefId(pick(pro, ["_id", "id"])) ||
            resolveRefId(pick(pro, ["projectid", "projectId", "ProjectId"])) ||
            resolveRefId(pick(proLeadLike, ["_id", "id"]))
        );

        // Prefer strict matching by IDs (reduces cross-bucket leakage).
        if (rowLeadId && proLeadId && rowLeadId === proLeadId) return true;
        if (rowQuotationId && proQuotationId && rowQuotationId === proQuotationId) return true;
        if (rowProjectId && proProjectId && rowProjectId === proProjectId) return true;

        // Fallback only if no stable IDs exist.
        const canFallbackByName = !rowLeadId && !rowQuotationId && !rowProjectId;
        if (!canFallbackByName) return false;
        return pro.TypeService === row.TypeService && pro.ProjectName === row.ProjectName;
      });

      matchedProjects.push({
        _id: row._id,
        ProjectName: row.ProjectName,
        TypeService: row.TypeService,
        oldProject: old,
        newProject: row,
      });

      setMatchedProjects(matchedProjects);
    }
  }, [projects, row]);

  const [allrowData, setAllrowData] = useState([]);
  console.log("OmShowAppprovedTasks allrowData", allrowData)

  const getTrainingCostRowForPerson = useCallback((project, emp) => {
    if (!project || !emp?._id) return null;

    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };
    const normKey = (v) => String(v || "").trim().toLowerCase();
    const pick = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k];
        if (val != null && String(val).trim() !== "") return val;
      }
      return "";
    };

    const list = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const leadLike = project?.lead || project?.Lead || null;
    const projectIdCandidates = new Set(
      [
        resolveRefId(pick(project, ["_id", "id"])),
        resolveRefId(pick(project, ["projectid", "projectId", "ProjectId"])),
        resolveRefId(pick(leadLike, ["_id", "id"])),
      ]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const quotationIdCandidates = new Set(
      [
        pick(project, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
        pick(leadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );
    const projectNameCandidates = new Set(
      [
        pick(project, ["ProjectName", "projectname"]),
        pick(leadLike, ["ProjectName", "projectname"]),
      ]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const leadIdCandidates = new Set(
      [
        pick(project, ["leadid", "leadId", "LeadId", "LeadID"]),
        pick(leadLike, ["leadid", "leadId", "LeadId", "LeadID"]),
        resolveRefId(pick(project, ["_id", "id"])),
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const assigneeId = resolveRefId(emp._id);
    const assigneeEmail = String(emp?.EmpEmail || "").trim().toLowerCase();

    const matched = list.filter((t) => {
      const assignedToRaw = t?.assignedto ?? t?.assignedTo;
      const assignedToId = resolveRefId(assignedToRaw);
      const assignedToEmail =
        typeof assignedToRaw === "string" ? String(assignedToRaw).trim().toLowerCase() : "";
      const matchesAssignee =
        (assigneeId && assignedToId && assignedToId === assigneeId) ||
        (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
      if (!matchesAssignee) return false;

      const tProjectId = normKey(resolveRefId(t?.projectid ?? t?.projectId ?? t?.project));
      const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
      const tProjectName = normKey(t?.projectname);
      const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

      // Match by ANY stable identifier first (lead / quotation / projectId).
      // QA data sometimes lacks leadid on training rows, so "lead-only" matching breaks.
      const stableMatch =
        (tLeadId && leadIdCandidates.has(tLeadId)) ||
        (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
        (tProjectId && projectIdCandidates.has(tProjectId));
      if (stableMatch) return true;

      // Fallback: only when the training row has no stable IDs at all.
      const trainingHasNoStableIds = !tLeadId && !tQuotationId && !tProjectId;
      if (!trainingHasNoStableIds) return false;
      return tProjectName && projectNameCandidates.has(tProjectName);
    });

    const normalized = matched
      .map((t) => {
        const amount = Number(
          String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, "")
        );
        const notes = t?.notes ?? t?.note ?? "";
        return { amount: Number.isFinite(amount) ? amount : 0, notes: String(notes || "").trim() };
      })
      .filter((x) => x.amount > 0 || String(x.notes || "").trim() !== "");

    const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
    const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
    if (total <= 0 && !joinedNotes) return null;

    return {
      _id: `training_cost_${assigneeId || assigneeEmail}`,
      taskownerid: assigneeId,
      description: "Training Cost",
      value: total,
      notes: joinedNotes,
      isTaskComplete: true,
      expenses: total,
    };
  }, [mytrainings]);
  useEffect(() => {
    if (
      matchedProjects?.length &&
      (entries?.length ||
        tasks?.length ||
        entries?.length)
    ) {
      const updated = matchedProjects?.map((proj) => {
        // old matches
        const oldworkEntries = entries.filter(
          (entry) => entry.projectid === proj.oldProject._id
        );
        const oldDescriptions = tasks.filter(
          (task) => task.projectid === proj.oldProject._id
        );

        // new matches
        const newworkEntries = entries.filter(
          (entry) => entry.projectid === proj.newProject._id
        );
        const newDescriptions = tasks.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        const newHardwareParts = items.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        return {
          _id: proj.newProject._id,
          TypeService: proj.newProject.TypeService,
          ProjectName: proj.newProject.ProjectName,
          Assignedperson: proj.newProject.Assignedperson,
          oldProject: {
            _id: proj.oldProject._id,
            ProjectName: proj.oldProject.ProjectName,
            TypeService: proj.oldProject.TypeService,
            Assignedperson: proj.oldProject.Assignedperson,
            oldworkEntries,
            oldDescriptions,
          },
          newProject: {
            _id: proj.newProject._id,
            ProjectName: proj.newProject.ProjectName,
            TypeService: proj.newProject.TypeService,
            Assignedperson: proj.newProject.Assignedperson,
            newworkEntries,
            newDescriptions,
            newHardwareParts: newHardwareParts,
          },
        };
      });

      setAllrowData(updated); // ✅ update state
    }
  }, [
    matchedProjects,
    entries,
    tasks,
    hardwareProjectTracking,
    items
  ]);

  const [unifitedList, setUnifitedList] = useState([]);
  const [, setActiveDropdown] = useState(null); // Changed state name for clarity
  const buttonsRef = useRef(null);
  console.log("OmShowAppprovedTasks unifitedList", unifitedList)

  // const processHardwareDescriptions = ({
  //   allDescriptions,
  //   employees,
  //   currentEmpId,
  // }) => {
  //   const empMap = employees.reduce((acc, e) => {
  //     acc[e._id] = e;
  //     return acc;
  //   }, {});

  //   const grouped = allDescriptions.reduce((acc, part) => {
  //     if (!acc[part.description]) acc[part.description] = [];
  //     acc[part.description].push(part);
  //     return acc;
  //   }, {});

  //   const result = [];

  //   Object.values(grouped).forEach(parts => {
  //     const pmParts = parts.filter(p =>
  //       empMap[p.taskownerid]?.customRoles?.includes("project manager")
  //     );

  //     const opParts = parts.filter(p =>
  //       empMap[p.taskownerid]?.customRoles?.includes("operations") &&
  //       !empMap[p.taskownerid]?.customRoles?.includes("project manager")
  //     );

  //     const highestPM = pmParts.length
  //       ? pmParts.reduce((a, b) => (b.value > a.value ? b : a))
  //       : null;

  //     const highestOP = opParts.length
  //       ? opParts.reduce((a, b) => (b.value > a.value ? b : a))
  //       : null;

  //     // 🔹 Operations keep their own
  //     opParts.forEach(p => {
  //       if (p.taskownerid === currentEmpId) {
  //         result.push(p);
  //       }
  //     });

  //     // 🔹 PM logic
  //     const isPM = empMap[currentEmpId]?.customRoles?.includes("project manager");
  //     if (!isPM) return;

  //     if (!highestPM && highestOP) {
  //       result.push({ ...highestOP });
  //       return;
  //     }

  //     if (
  //       highestPM &&
  //       (!highestOP || highestPM.value >= highestOP.value)
  //     ) {
  //       if (highestPM.taskownerid === currentEmpId) {
  //         result.push(highestPM);
  //       }
  //       return;
  //     }

  //     if (highestPM && highestOP && highestOP.value > highestPM.value) {
  //       result.push({ ...highestOP });
  //     }
  //   });

  //   return result.filter(
  //     (v, i, self) =>
  //       i === self.findIndex(x => x.description === v.description)
  //   );
  // };

  const sumDescriptionsForManager = ({
    allDescriptions,
    employees,
    currentEmpId,
  }) => {
    const isPM = employees.find(e => e._id === currentEmpId)
      ?.customRoles?.includes("project manager");

    // If not manager → return only own entries
    if (!isPM) {
      return allDescriptions.filter(
        d => d.taskownerid === currentEmpId
      );
    }

    const grouped = allDescriptions.reduce((acc, item) => {
      if (!acc[item.description]) {
        acc[item.description] = {
          ...item,
          value: 0,
        };
      }
      acc[item.description].value += Number(item.value || 0);
      return acc;
    }, {});

    return Object.values(grouped);
  };


  useEffect(() => {
    if (allrowData?.length && employees?.length) {
      const mergedData = allrowData?.map((project) => {
        const assignedData = project.newProject.Assignedperson?.map((person) => {
          const emp = employees.find((e) => e.EmpEmail === person.email);
          if (!emp) return null; // skip if no match

          // 2️⃣ Collect role + id
          const empid = emp._id;
          const role = emp.customRoles?.[0] || null;
          const name = `${emp.EmpFirstName} ${emp.EmpLastName}`;

          // 3️⃣ Match old/new work entries and descriptions by taskownerid
          const oldworkEntries = project.oldProject.oldworkEntries.filter(
            (entry) => entry.taskownerid === empid
          );
          const newworkEntries = project.newProject.newworkEntries.filter(
            (entry) => entry.taskownerid === empid
          );

          const oldDescriptions = project.oldProject.oldDescriptions.filter(
            (desc) => desc.taskownerid === empid
          );
          // const newDescriptions = project.newProject.newDescriptions.filter(
          //   (desc) => desc.taskownerid === empid
          // );

          let newDescriptions = [];

          if (project.newProject?.TypeService === "Hardware") {
            newDescriptions = sumDescriptionsForManager({
              allDescriptions: project.newProject?.newDescriptions || [],
              employees,
              currentEmpId: empid,
            });
            const hasTrainingAlready = (newDescriptions || []).some(
              (d) => isTrainingExpenseDisplayRow(d)
            );
            if (!hasTrainingAlready) {
              const trainingRow = getTrainingCostRowForPerson(project.newProject, emp);
              if (trainingRow) newDescriptions = [...newDescriptions, trainingRow];
            }
            newDescriptions = consolidateTrainingExpenseRowsForDisplay(newDescriptions);
          } else {
            let descRows = [
              ...(project.newProject?.newDescriptions.filter(
                (desc) => desc.taskownerid === empid
              ) || []),
            ];
            const liveRollup = getLiveTrainingRollupForOpsAssigneeProject({
              projectRow: project.newProject,
              assigneeEmp: emp,
              trainingsEnvelope: mytrainings,
              projects,
              currencySymbol: currency,
            });
            const liveAmt = liveRollup ? Number(liveRollup.amount) || 0 : 0;
            const liveNotes = String(liveRollup?.notes || "").trim();
            const hasTrainingInBase = descRows.some((d) => isTrainingExpenseDisplayRow(d));
            if (liveAmt > 0 && !hasTrainingInBase && emp?._id) {
              descRows.push({
                _id: `training_cost_${String(emp._id).trim()}-${String(project.newProject?._id || "").trim()}`,
                projectid: project.newProject._id,
                taskownerid: emp._id,
                description: liveRollup.description || `Training Cost (${currency})`,
                value: liveAmt,
                notes: liveNotes,
                isTaskComplete: true,
                expenses: liveAmt,
              });
            }
            newDescriptions = consolidateTrainingExpenseRowsForDisplay(descRows);
            if (liveAmt > 0 && newDescriptions.some((d) => isTrainingExpenseDisplayRow(d))) {
              newDescriptions = applyLiveTrainingTotalToOtherExpenseRows(
                newDescriptions,
                liveAmt,
                liveNotes,
                currency
              );
            }
          }

          newDescriptions = filterInstallationOngoingOtherExpenseDisplayRows(
            newDescriptions,
            project.newProject
          );

          const newHardwareParts = project.newProject.newHardwareParts.filter(
            (desc) => desc.taskownerid === empid
          );
          return {
            TypeService: project.TypeService,
            name,
            empid,
            role,
            oldworkEntries,
            newworkEntries,
            oldDescriptions,
            newDescriptions,
            newHardwareParts,
          };
        }).filter(Boolean) // remove nulls (non-matching employees)
          .sort((a, b) => (a.role === "project manager" ? -1 : b.role === "project manager" ? 1 : 0));

        if (project.newProject?.TypeService === "Hardware") {

          // 1️⃣ Create employee map for quick lookup
          const empMap = assignedData.reduce((acc, e) => {
            acc[e.empid] = e;
            return acc;
          }, {});

          // 2️⃣ Collect ALL newHardwareParts
          const allNewHardwareParts = assignedData.flatMap(
            e => e.newHardwareParts || []
          );

          // 3️⃣ Group by assemblypartdescription
          const groupedByAssembly = allNewHardwareParts.reduce((acc, part) => {
            const key = part.assemblepartdescription;
            if (!acc[key]) acc[key] = [];
            acc[key].push(part);
            return acc;
          }, {});

          // 4️⃣ Reset processed hardware array
          assignedData.forEach(e => {
            e.hardwarePartsOngoing = [];
          });

          // 5️⃣ Find Project Manager
          const pmEmp = assignedData.find(
            e => e.role === "project manager"
          );

          // 6️⃣ Apply rules
          Object.entries(groupedByAssembly).forEach(([desc, parts]) => {
            if (!pmEmp) return;

            const pmParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "project manager"
            );

            const opParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "operations"
            );

            // ✅ Operations always keep their own parts
            opParts.forEach(part => {
              const opEmp = empMap[part.taskownerid];
              if (opEmp) opEmp.hardwarePartsOngoing.push(part);
            });

            // ✅ Decide PM entry
            if (pmParts.length && opParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push(
                Number(highestOP.quantity) > Number(highestPM.quantity)
                  ? { ...highestOP }
                  : { ...highestPM }
              );

            } else if (!pmParts.length && opParts.length) {
              // 🔥 OP only → clone to PM
              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestOP });

            } else if (pmParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestPM });
            }
          });

          // 7️⃣ OPTIONAL: replace original newHardwareParts with processed data
          assignedData.forEach(e => {
            e.newHardwareParts = e.hardwarePartsOngoing;
          });
        }

        return {
          ...project,
          AssignedPersonData: assignedData, // ✅ merged data per employee
        };
      });
      setUnifitedList(mergedData);
    }
  }, [
    allrowData,
    employees,
    getTrainingCostRowForPerson,
    mytrainings,
    projects,
    currency,
  ]);

  const downloadPDFHardware = (newHardwareParts) => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Part Name",
          "Part Number",
          "Quantity",
          `Unit Price (${currency})`,
          `Total Price (${currency})`,
        ],
      ],
      body: newHardwareParts?.map((LeadReport, index) => [
        index + 1,
        LeadReport.assemblepartdescription,
        LeadReport.partno,
        LeadReport.quantity,
        USAComma(LeadReport.unitprice),
        USAComma(LeadReport.totalprice),
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware PartList.pdf");
  };

  const downloadPDFHardwareDescriptions = (newDescriptions) => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [["Sr. No", "Description", `Amount (${currency})`, "Notes"]],
      body: newDescriptions?.map((LeadReport, index) => [
        index + 1,
        LeadReport.description,
        USAComma(LeadReport.value),
        LeadReport.notes,
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware OtherExpensesList.pdf");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonsRef.current && !buttonsRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [currentProject, setCurrentProject] = useState({})
  const [clickedIdPerson, setClickedIdPerson] = useState(null)
  console.log("OmShowAppprovedTasks currentProject", currentProject)

  useEffect(() => {
    if (!unifitedList?.length) return;
    for (const project of unifitedList) {
      const manager = project?.AssignedPersonData?.find(
        (p) => p.role === "project manager"
      );
      if (manager) {
        setCurrentProject(manager);
        setClickedIdPerson(manager.empid);
        break; // stop once found
      }
    }
  }, [unifitedList]); // run when data loads

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex w-full ">
      <div className="w-[23%] flex justify-center items-start p-2 border-b ">
        {unifitedList?.map((project, projectIndex) => (
          <div key={projectIndex} className="w-full flex">
            <div className="w-[100%] flex flex-col ">
              {project.AssignedPersonData?.map((proj, index) => {
                return (
                  <div ref={buttonsRef} key={index} className="w-[100%] mb-2 flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentProject(currentProject === proj ? null : proj);
                        setClickedIdPerson(clickedIdPerson === proj.empid ? null : proj.empid);
                      }}
                      className={`
      w-full h-[1.5rem] border text-[12px] rounded-md shadow-md text-start px-1 overflow-hidden truncate
      ${clickedIdPerson === proj.empid
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white border-gray-400"
                        }
    `}
                      title={proj.role === "project manager" ? "Self" : proj.name} // full name on hover
                    >
                      {proj.role === "project manager" ? "Self" : proj.name}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="w-[77%] p-2">

        {currentProject?.TypeService?.toLowerCase().includes("hardware")
          ? showListState === "tasktable" && (
            <>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="w-full rounded-lg h-full justify-center pb-5"
              >
                <div className="w-full border border-gray-300 overflow-hidden">
                  <div className="grid grid-cols-3 bg-blue-500 text-white text-[11px]">
                    <div className="p-1 border-r  text-center">
                      Total Parts
                    </div>
                    <div className="p-1 border-r text-center">
                      Total Price ({currency})
                    </div>
                    <div className="p-1 text-center">Action</div>
                  </div>
                  <div className="grid grid-cols-3 bg-white text-black text-[11px]">
                    <div className="p-1 border-r border-blue-200 text-center">
                      {currentProject?.newHardwareParts?.length}
                    </div>
                    <div className="p-1 border-r border-blue-200 text-center">
                      {USAComma(
                        currentProject?.newHardwareParts?.reduce(
                          (sum, item) => {
                            return sum + (item.totalprice || 0);
                          },
                          0
                        )
                      )}{" "}
                    </div>
                    <div
                      title="Download Hardware Part List"
                      className="p-1 text-center cursor-pointer hover:bg-blue-50"
                      onClick={() =>
                        downloadPDFHardware(
                          currentProject?.newHardwareParts
                        )
                      }
                    >
                      Download
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
          : showListState === "tasktable" && (
            <div className="w-full flex  gap-2">
              <div className="w-full max-h-[14rem] overflow-auto">
                <div className="sticky top-0 bg-[#1973eb] text-white text-[11px] text-center">
                  <div className="flex">
                    <div className="w-[35%] border p-1">Task</div>
                    <div className="w-[30%] border p-1">
                      Hours Assigned
                    </div>
                    <div className="w-[35%] border p-1">
                      Notes
                    </div>
                  </div>
                  <div className="flex bg-white text-black">
                    <div className="w-[35%] border p-1">
                      Total
                    </div>
                    <div className="w-[30%] border p-1">
                      {formatAmountComma(
                        currentProject?.newworkEntries?.reduce(
                          (sum, item) =>
                            sum + parseAmountNumber(item.hoursassigned),
                          0
                        ) || 0
                      )}
                    </div>

                    <div className="w-[35%] border p-1"></div>
                  </div>
                </div>
                {currentProject?.newworkEntries?.map((entry, index) => {
                  const matchedOldWorkEntry = entries?.find(
                    (old) => old._id === entry.oldtaskid
                  );
                  return (
                    <div
                      key={index}
                      className="flex text-center text-[11px]"
                    >
                      <div
                        className="w-[35%] border p-1"
                        title={
                          matchedOldWorkEntry
                            ? `Previous Name: ${matchedOldWorkEntry.task}`
                            : ""
                        }
                      >
                        {entry.task}
                      </div>
                      <div
                        className="w-[30%] border p-1"
                        title={
                          matchedOldWorkEntry
                            ? `Previous Hrs: ${matchedOldWorkEntry.hoursassigned}`
                            : ""
                        }
                      >
                        {entry.hoursassigned}
                      </div>
                      <div
                        className="w-[35%] border p-1"
                        title={
                          matchedOldWorkEntry
                            ? `Previous Notes: ${matchedOldWorkEntry.notes}`
                            : ""
                        }
                      >
                        {entry.notes}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {showListState === "otherexpenses" &&
          (currentProject?.TypeService?.toLowerCase().includes("hardware") ? (
            <div className="w-full border border-gray-300 overflow-hidden">
              <div className="grid grid-cols-3 bg-blue-500 text-white text-[11px]">
                <div className="p-1 border-r  text-center">
                  Total Descriptions{" "}
                </div>
                <div className="p-1 border-r text-center">
                  Total Amount ({currency})
                </div>
                <div className="p-1 text-center">Action</div>
              </div>

              <div className="grid grid-cols-3 bg-white text-black text-[11px]">
                <div className="p-1 border-r border-blue-200 text-center">
                  {currentProject?.newDescriptions?.length}
                </div>
                <div className="p-1 border-r border-blue-200 text-center">
                  {formatAmountComma(
                    currentProject?.newDescriptions?.reduce((sum, item) => {
                      return sum + parseAmountNumber(item.value);
                    }, 0)
                  )}{" "}
                </div>
                <div
                  title="Download Description List"
                  className="p-1 text-center cursor-pointer hover:bg-blue-50"
                  onClick={() =>
                    downloadPDFHardwareDescriptions(
                      currentProject?.newDescriptions
                    )
                  }
                >
                  Download
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full flex  gap-2">
              <div className="w-full max-h-[14rem] overflow-auto">
                <div className="sticky top-0 bg-[#1973eb] text-white text-[11px] text-center">
                  <div className="flex">
                    {" "}
                    <div className="w-[40%] border p-1 truncate">
                      Description
                    </div>
                    <div className="w-[30%] border p-1">
                      Amount ({currency})
                    </div>
                    <div className="w-[30%] border p-1">Notes</div>
                  </div>

                  {/* Trip Duration row for installation projects */}
                  {currentProject?.TypeService && 
                   (Array.isArray(currentProject.TypeService)
                     ? currentProject.TypeService.some(s => String(s || "").toLowerCase().includes("installation"))
                     : String(currentProject.TypeService || "").toLowerCase().includes("installation")) ? (
                    <div className="flex text-center text-[11px] bg-blue">
                      <div className="w-[40%] border p-1">
                        Trip Duration
                      </div>
                      <div className="w-[30%] border p-1">
                        {(() => {
                          // Get trip duration from descriptions
                          const tripDurations = currentProject?.newDescriptions
                            ?.filter(desc => desc.tripduration)
                            ?.map(desc => desc.tripduration) || [];
                          const maxTripDuration = tripDurations.length > 0 
                            ? Math.max(...tripDurations) 
                            : null;
                          return maxTripDuration ? maxTripDuration : "";
                        })()}
                      </div>
                      <div className="w-[30%] border p-1"></div>
                    </div>
                  ) : null}

                  <div className="flex bg-white text-black">
                    <div className="w-[40%] border p-1">Total</div>
                    <div className="w-[30%] border p-1">
                      {formatAmountComma(
                        currentProject?.newDescriptions?.reduce(
                          (sum, item) => sum + parseAmountNumber(item.value),
                          0
                        ) || 0
                      )}
                    </div>
                    <div className="w-[30%] border p-1"></div>
                  </div>
                </div>
                {currentProject?.newDescriptions?.map((description, index) => {
                  const matchedOldDescription = tasks.find(
                    (old) => old._id === description.oldtaskid
                  );
                  return (
                    <div
                      key={index}
                      className="flex text-center text-[11px]"
                    >
                      <div
                        className="w-[40%] border p-1"
                        title={
                          matchedOldDescription
                            ? `Previous Name: ${matchedOldDescription.description}`
                            : ""
                        }
                      >
                        {description.description}
                      </div>
                      <div
                        className="w-[30%] border p-1"
                        title={
                          matchedOldDescription
                            ? `Previous Value: ${formatAmountComma(matchedOldDescription.value)}}`
                            : ""
                        }
                      >
                        {formatAmountComma(description.value)}
                      </div>
                      <div
                        className="w-[30%] border p-1"
                        title={
                          matchedOldDescription
                            ? `Previous Note: ${matchedOldDescription.notes}`
                            : ""
                        }
                      >
                        {description.notes}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default OmShowAppprovedTasks;